/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Mark, mergeAttributes } from "@tiptap/core";
import { registerCustomProtocol, reset } from "linkifyjs";
import { isAllowedUri } from "./newer-tiptap/tiptap-link";
import { clickHandler } from "./click-handler.js";
import { pasteHandler } from "./paste-handler.js";

const SimpliCityLink = Mark.create({

    name: "link",

    priority: 1000,

    keepOnSplit: false,

    selectable: false,

    atom: true,

    onCreate () {
        this.options.protocols.forEach((protocol) => {
            if (typeof protocol === "string") {
                registerCustomProtocol(protocol);
                return;
            }
            registerCustomProtocol(protocol.scheme, protocol.optionalSlashes);
        });
    },

    onDestroy () {
        reset();
    },

    inclusive () {
        return this.options.autolink;
    },

    addOptions () {
        return {
            openOnClick: true,
            linkOnPaste: false, // allow creating link when pasted 
            autolink: false,
            protocols: [],
            defaultProtocol: "https",
            HTMLAttributes: {
                guid: null,
                type: "link",
                link_type: "internal",
                external: false,
                target: "_blank",
                rel: "noopener noreferrer nofollow",
                class: null,
            },
            isAllowedUri: (url, ctx) => !!isAllowedUri(url, ctx.protocols),
            validate: (url) => !!url,
            shouldAutoLink: (url) => !!url,

        };
    },

    addAttributes () {
        return {
            href: {
                default: null,
            },
            url: {
                default: null,
            },
            type: {
                default: "link",
            },
            link_type: {
                default: "internal",
            },
            external: {
                default: false,
            },
            guid: {
                default: null,
            },
            text: {
                default: null,
            },
            webpage: {
                default: null,
            },
            target: {
                default: this.options.HTMLAttributes.target,
            },
            rel: {
                default: this.options.HTMLAttributes.rel,
            },
            class: {
                default: this.options.HTMLAttributes.class,
            },
        };
    },
    
    
    // This is commented out to disable converting a href in html to half-created links
    // parseHTML () {
    //     return [
    //         {
    //             tag: "a[href]",
    //             getAttrs: (dom) => {
    //                 const href = dom.getAttribute("href");
    //                
    //                 // prevent XSS attacks
    //                 const allowedUriOptions = {
    //                     defaultValidate: (url) => !!isAllowedUri(url, this.options.protocols),
    //                     protocols: this.options.protocols,
    //                     defaultProtocol: this.options.defaultProtocol,
    //                 };
    //                 const notAllowedUri = !href || !this.options.isAllowedUri(href, allowedUriOptions);
    //                 if (notAllowedUri) {
    //                     return false;
    //                 }
    //                 return null;
    //             },
    //         },
    //     ];
    // },

    renderHTML ({ HTMLAttributes }) {
        // console.info(`[RichText SimplicityLink] HTMLAttributes: ]`, HTMLAttributes);
        const attrs = mergeAttributes(
            { "data-type": this.name },
            this.options.HTMLAttributes,
            HTMLAttributes
        );
        return ["a", attrs, 0];
    },

    // addNodeView() {
    //     return ReactNodeViewRenderer(LinkNode)
    // },

    addCommands () {
        return {
            setLink: (attributes) => ({ chain }) => {
                // console.log(`[RichText SimplicityLink] setLink`, attributes);
                return chain().setMark(SimpliCityLink.name, attributes)
                    .setMeta("preventAutolink", true)
                    .run();
            },
            toggleLink: (attributes) => ({ chain }) => {
                // console.log(`[RichText SimplicityLink] toggleLink`, attributes);
                return chain()
                    .toggleMark(SimpliCityLink.name, attributes, { extendEmptyMarkRange: true })
                    .setMeta("preventAutolink", true)
                    .run();
            },
            unsetLink: () => ({ chain }) => {
                // console.log(`[RichText SimplicityLink] unsetLink`);
                return chain()
                    .unsetMark(SimpliCityLink.name, { extendEmptyMarkRange: true })
                    .setMeta("preventAutolink", true)
                    .run();
            },
        };
    },
    
    /* 
        This function can find links in pasted text
        It can only create rich a text link mark -> that is not connected to a simplicity Link
        
        Maybe we can show a dialog of the list of links offering to create them 
    */ 
    // addPasteRules () {
    //     return [
    //         markPasteRule({
    //             find: (text) => {
    //                
    //                 const foundLinks = [];
    //                
    //                 if (text) {
    //                     const protocols = this.options?.protocols;
    //                     const defaultProtocol = this.options?.defaultProtocol;
    //                     const links = find(text).filter((item) =>
    //                         item.isLink &&
    //                         this.options.isAllowedUri(item.value, {
    //                             defaultValidate: (href) => {
    //                                 return !!isAllowedUri(href, protocols);
    //                             },
    //                             protocols,
    //                             defaultProtocol,
    //                         }),
    //                     );
    //
    //                     if (links.length) {
    //                         links.forEach((link) =>
    //                             foundLinks.push({
    //                                 text: link.value,
    //                                 data: {
    //                                     href: link.href,
    //                                 },
    //                                 index: link.start,
    //                             }),
    //                         );
    //                     }
    //                 }
    //                
    //                 return foundLinks;
    //             },
    //             type: this.type,
    //             getAttributes: (match) => {
    //                 return {
    //                     href: match.data?.href,
    //                 };
    //             },
    //         }),
    //     ];
    // },

    addProseMirrorPlugins () {
        const plugins = [];
        // if (this.options.autolink) {
        //     plugins.push(autolink({
        //         type: this.type,
        //         validate: this.options.validate,
        //     }));
        // }
        if (this.options.openOnClick) {
            plugins.push(clickHandler({
                type: this.type,
            }));
        }
        if (this.options.linkOnPaste) {
            plugins.push(pasteHandler({
                editor: this.editor,
                type: this.type,
            }));
        }
        return plugins;
    },
});

export { SimpliCityLink, SimpliCityLink as default };
